/**
 * Menu Builder Store
 * 
 * Zustand store for menu builder state management.
 * Includes undo/redo functionality, block management, and UI state.
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { useShallow } from 'zustand/react/shallow';
import { nanoid } from 'nanoid';
import { 
  BlockType, 
  AnyMenuBlock, 
  MenuBuilderLayout,
  ViewMode,
  getDefaultBlockProps,
  createEmptyLayout,
} from '@/types/menuBuilder';

// ============================================================================
// Types
// ============================================================================

interface MenuBuilderState {
  // Layout data
  blocks: AnyMenuBlock[];
  globalThemeOverrides: MenuBuilderLayout['globalThemeOverrides'];
  
  // UI state
  selectedBlockId: string | null;
  hoveredBlockId: string | null;
  viewMode: ViewMode;
  isDirty: boolean;
  
  // Drag state
  isDragging: boolean;
  draggedBlockId: string | null;
  
  // History for undo/redo
  history: AnyMenuBlock[][];
  historyIndex: number;
  maxHistorySize: number;
  
  // Actions - Block management
  addBlock: (type: BlockType, position?: number) => string;
  removeBlock: (blockId: string) => void;
  moveBlock: (fromIndex: number, toIndex: number) => void;
  duplicateBlock: (blockId: string) => string | null;
  updateBlockProps: <T extends BlockType>(blockId: string, props: Partial<AnyMenuBlock['props']>) => void;
  
  // Actions - Selection
  selectBlock: (blockId: string | null) => void;
  hoverBlock: (blockId: string | null) => void;
  
  // Actions - Drag
  setDragging: (isDragging: boolean, blockId?: string | null) => void;
  
  // Actions - Undo/Redo
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  
  // Actions - Layout
  loadLayout: (layout: MenuBuilderLayout) => void;
  getLayout: () => MenuBuilderLayout;
  reset: () => void;
  
  // Actions - UI
  setViewMode: (mode: ViewMode) => void;
  markClean: () => void;
  
  // Actions - Theme
  updateGlobalTheme: (overrides: Partial<MenuBuilderLayout['globalThemeOverrides']>) => void;
}

// ============================================================================
// Helpers
// ============================================================================

function saveToHistory(state: MenuBuilderState) {
  // Truncate any future history if we're not at the end
  const newHistory = state.history.slice(0, state.historyIndex + 1);
  
  // Add current state to history
  newHistory.push(JSON.parse(JSON.stringify(state.blocks)));
  
  // Limit history size
  if (newHistory.length > state.maxHistorySize) {
    newHistory.shift();
  }
  
  state.history = newHistory;
  state.historyIndex = newHistory.length - 1;
}

function reorderBlocks(blocks: AnyMenuBlock[]) {
  blocks.forEach((block, index) => {
    block.order = index;
  });
}

// ============================================================================
// Store
// ============================================================================

export const useMenuBuilderStore = create<MenuBuilderState>()(
  immer((set, get) => ({
    // Initial state
    blocks: [],
    globalThemeOverrides: undefined,
    selectedBlockId: null,
    hoveredBlockId: null,
    viewMode: 'mobile',
    isDirty: false,
    isDragging: false,
    draggedBlockId: null,
    history: [[]],
    historyIndex: 0,
    maxHistorySize: 50,

    // ========================================================================
    // Block Management
    // ========================================================================

    addBlock: (type, position) => {
      const newId = nanoid(10);
      
      set((state) => {
        const newBlock: AnyMenuBlock = {
          id: newId,
          type,
          props: getDefaultBlockProps(type),
          order: position ?? state.blocks.length,
        } as AnyMenuBlock;

        if (position !== undefined && position >= 0 && position <= state.blocks.length) {
          state.blocks.splice(position, 0, newBlock);
        } else {
          state.blocks.push(newBlock);
        }

        reorderBlocks(state.blocks);
        state.isDirty = true;
        state.selectedBlockId = newId;
        saveToHistory(state);
      });
      
      return newId;
    },

    removeBlock: (blockId) => {
      set((state) => {
        const index = state.blocks.findIndex((b) => b.id === blockId);
        if (index === -1) return;

        state.blocks.splice(index, 1);
        reorderBlocks(state.blocks);

        if (state.selectedBlockId === blockId) {
          state.selectedBlockId = null;
        }
        
        state.isDirty = true;
        saveToHistory(state);
      });
    },

    moveBlock: (fromIndex, toIndex) => {
      set((state) => {
        if (fromIndex < 0 || fromIndex >= state.blocks.length) return;
        if (toIndex < 0 || toIndex >= state.blocks.length) return;
        if (fromIndex === toIndex) return;

        const [removed] = state.blocks.splice(fromIndex, 1);
        state.blocks.splice(toIndex, 0, removed);
        reorderBlocks(state.blocks);
        
        state.isDirty = true;
        saveToHistory(state);
      });
    },

    duplicateBlock: (blockId) => {
      const state = get();
      const block = state.blocks.find((b) => b.id === blockId);
      if (!block) return null;

      const newId = nanoid(10);
      
      set((state) => {
        const index = state.blocks.findIndex((b) => b.id === blockId);
        const newBlock = {
          ...JSON.parse(JSON.stringify(block)),
          id: newId,
        };

        state.blocks.splice(index + 1, 0, newBlock);
        reorderBlocks(state.blocks);
        
        state.selectedBlockId = newId;
        state.isDirty = true;
        saveToHistory(state);
      });

      return newId;
    },

    updateBlockProps: (blockId, props) => {
      set((state) => {
        const blockIndex = state.blocks.findIndex((b) => b.id === blockId);
        if (blockIndex === -1) return;

        // Type-safe update using Object.assign
        const block = state.blocks[blockIndex];
        Object.assign(block.props, props);
        state.isDirty = true;
        saveToHistory(state);
      });
    },

    // ========================================================================
    // Selection
    // ========================================================================

    selectBlock: (blockId) => {
      set((state) => {
        state.selectedBlockId = blockId;
      });
    },

    hoverBlock: (blockId) => {
      set((state) => {
        state.hoveredBlockId = blockId;
      });
    },

    // ========================================================================
    // Drag
    // ========================================================================

    setDragging: (isDragging, blockId = null) => {
      set((state) => {
        state.isDragging = isDragging;
        state.draggedBlockId = blockId;
      });
    },

    // ========================================================================
    // Undo/Redo
    // ========================================================================

    undo: () => {
      set((state) => {
        if (state.historyIndex > 0) {
          state.historyIndex -= 1;
          state.blocks = JSON.parse(JSON.stringify(state.history[state.historyIndex]));
          state.isDirty = true;
        }
      });
    },

    redo: () => {
      set((state) => {
        if (state.historyIndex < state.history.length - 1) {
          state.historyIndex += 1;
          state.blocks = JSON.parse(JSON.stringify(state.history[state.historyIndex]));
          state.isDirty = true;
        }
      });
    },

    canUndo: () => {
      const state = get();
      return state.historyIndex > 0;
    },

    canRedo: () => {
      const state = get();
      return state.historyIndex < state.history.length - 1;
    },

    // ========================================================================
    // Layout
    // ========================================================================

    loadLayout: (layout) => {
      set((state) => {
        state.blocks = layout.blocks || [];
        state.globalThemeOverrides = layout.globalThemeOverrides;
        state.selectedBlockId = null;
        state.isDirty = false;
        state.history = [JSON.parse(JSON.stringify(state.blocks))];
        state.historyIndex = 0;
      });
    },

    getLayout: () => {
      const state = get();
      return {
        version: '1.0',
        globalThemeOverrides: state.globalThemeOverrides,
        blocks: state.blocks,
      };
    },

    reset: () => {
      set((state) => {
        state.blocks = [];
        state.globalThemeOverrides = undefined;
        state.selectedBlockId = null;
        state.hoveredBlockId = null;
        state.isDirty = false;
        state.isDragging = false;
        state.draggedBlockId = null;
        state.history = [[]];
        state.historyIndex = 0;
      });
    },

    // ========================================================================
    // UI
    // ========================================================================

    setViewMode: (mode) => {
      set((state) => {
        state.viewMode = mode;
      });
    },

    markClean: () => {
      set((state) => {
        state.isDirty = false;
      });
    },

    // ========================================================================
    // Theme
    // ========================================================================

    updateGlobalTheme: (overrides) => {
      set((state) => {
        state.globalThemeOverrides = {
          ...state.globalThemeOverrides,
          ...overrides,
        };
        state.isDirty = true;
        saveToHistory(state);
      });
    },
  }))
);

// ============================================================================
// Selectors
// ============================================================================

/**
 * Get selected block
 */
export const useSelectedBlock = () => {
  return useMenuBuilderStore((state) => {
    if (!state.selectedBlockId) return null;
    return state.blocks.find((b) => b.id === state.selectedBlockId) || null;
  });
};

/**
 * Get block by ID
 */
export const useBlock = (blockId: string) => {
  return useMenuBuilderStore((state) => 
    state.blocks.find((b) => b.id === blockId) || null
  );
};

/**
 * Get all blocks
 */
export const useBlocks = () => {
  return useMenuBuilderStore((state) => state.blocks);
};

/**
 * Get blocks sorted by order
 */
export const useSortedBlocks = () => {
  return useMenuBuilderStore(
    useShallow((state) => 
      [...state.blocks].sort((a, b) => a.order - b.order)
    )
  );
};

/**
 * Check if a block is selected
 */
export const useIsBlockSelected = (blockId: string) => {
  return useMenuBuilderStore((state) => state.selectedBlockId === blockId);
};

/**
 * Check if a block is being dragged
 */
export const useIsBlockDragging = (blockId: string) => {
  return useMenuBuilderStore((state) => state.draggedBlockId === blockId);
};

/**
 * Get undo/redo state
 */
export const useUndoRedo = () => {
  return useMenuBuilderStore(
    useShallow((state) => ({
      canUndo: state.historyIndex > 0,
      canRedo: state.historyIndex < state.history.length - 1,
      undo: state.undo,
      redo: state.redo,
    }))
  );
};

/**
 * Get dirty state
 */
export const useIsDirty = () => {
  return useMenuBuilderStore((state) => state.isDirty);
};
